import { routes } from "@/router";
import { useTranslation } from "react-i18next";
import { HiOutlinePencilSquare, HiOutlinePlus } from "react-icons/hi2";
import { generatePath, Link, useNavigate } from "react-router-dom";

import { Product, useGetAllProducts } from "@/hooks/api/predictive/product/useGetAllProducts";
import Button from "@/components/atoms/Button";
import CampaignCard from "@/components/molecules/predictive/CampaignCard";
import CreateCampaignCard from "@/components/molecules/predictive/CreateCampaignCard";
import PredictiveLeadsHeader from "@/components/organisms/predictive/PredictiveLeadsHeader";

const ProductsOverview = () => {
  const { t } = useTranslation();
  return (
    <div className={"w-full overflow-y-auto px-6 pb-16"}>
      <PredictiveLeadsHeader showIntroButton />

      <ProductCampaignsSection />

      <div className={"mb-2 border-b"} />

      <Link
        to={routes.product.create}
        className={"my-2 inline-flex items-center gap-2 p-2 text-sm font-medium text-quinary hover:text-quinary-hover"}
      >
        {t("predictive.product.create")} <HiOutlinePlus className={"size-5"} />
      </Link>
    </div>
  );
};

export default ProductsOverview;

type ProductCampaignsItemProps = {
  product: Product;
};

const ProductCampaignsItem = ({ product }: ProductCampaignsItemProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className={"pb-6"}>
      <div className={"mb-3 flex items-center gap-2"}>
        <h4 className={"text-xl font-semibold text-primary"}>{product.name}</h4>
        <Button
          label={t("predictive.product.edit")}
          size={"xs"}
          variants={"textLight"}
          color={"natural"}
          className={"!px-1.5 !pb-0 !pt-0.5"}
          trailingIcon={<HiOutlinePencilSquare className={"size-4"} />}
          onClick={() => navigate(generatePath(routes.product.update, { productId: product.id }))}
        />
      </div>
      <div className={"grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"}>
        {product.campaigns.map((campaign) => (
          <CampaignCard key={campaign.id} productId={product.id} campaign={campaign} />
        ))}

        <CreateCampaignCard productId={product.id} />
      </div>
    </div>
  );
};

const ProductCampaignsSection = () => {
  const { data: products, isLoading } = useGetAllProducts();

  if (isLoading) {
    return (
      <div>
        {Array.from({ length: 2 }).map((_, index) => (
          <div key={index} className={"mb-6"}>
            <div className={"mb-3 flex items-center gap-2"}>
              <div className={"h-6 w-56 animate-pulse rounded-lg bg-gray-200"} />
              <div className={"h-4 w-20 animate-pulse rounded-lg bg-gray-200"} />
            </div>
            <div className={"grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"}>
              {Array.from({ length: 2 }).map((_, idx) => (
                <div key={idx} className={"h-56 animate-pulse rounded-lg bg-gray-200"} />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className={"py-10"}>
        <p className={"text-sm text-quaternary"}>No products or services found. Please add one to get started.</p>
      </div>
    );
  }

  return products.map((product) => <ProductCampaignsItem key={product.id} product={product} />);
};
